import { Injectable } from '@angular/core';

import { DateRange, Status } from '@enums';
import { Vehicle, VehicleBatch, VehicleEvent } from '@models';
import { Utils } from '@utils';
import { environment } from 'src/environment/environment';
import { CrudService } from '../crud/crud.service';
import { HttpService } from '../http/http.service';

@Injectable({
  providedIn: 'root',
})
export class VehicleService extends CrudService<Vehicle> {
  constructor(http: HttpService) {
    super('vehicle', http);
  }

  async assignUserToVehicle(
    vehicleId: string,
    userId: string
  ): Promise<Vehicle> {
    const endpoint = `${environment.apiUrl}/admin/vehicle/assign-user`;
    const response = await this.http.post<Vehicle>(endpoint, {
      userId,
      vehicleId,
    });
    if (!response.success) throw new Error(response.message);
    return response.data;
  }

  async removeUserFromVehicle(
    vehicleId: string,
    userId: string
  ): Promise<Vehicle> {
    const endpoint = `${environment.apiUrl}/admin/vehicle/remove-user`;
    const response = await this.http.post<Vehicle>(endpoint, {
      userId,
      vehicleId,
    });
    if (!response.success) throw new Error(response.message);
    return response.data;
  }

  // fetch vehicle events
  async getVehicleEvents(
    carTrackDriverId: string,
    dateRange: DateRange
  ): Promise<VehicleEvent[]> {
    const { from, to } = Utils.fromDateRange(dateRange);
    const endpoint = `${environment.apiUrl}/vehicle/user/events/${carTrackDriverId}/${from}/${to}`;
    const response = await this.http.get<VehicleEvent[]>(endpoint);
    if (!response.success) throw new Error(response.message);
    return response.data;
  }

  async setVehicleStatus(vehicle: Vehicle, status: Status): Promise<Vehicle> {
    const endpoint = `${environment.apiUrl}/vehicle/status`;
    const response = await this.http.post<Vehicle>(endpoint, {
      vehicleId: vehicle.id,
      status,
    });
    if (!response.success) throw new Error(response.message);
    return response.data;
  }

  async getBatches(): Promise<VehicleBatch[]> {
    const response = await this.http.get<VehicleBatch[]>(
      `${this.url}/batch/all`
    );
    if (!response.success) throw new Error(response.message);
    return response.data;
  }

  async getBatchById(batchId: string): Promise<VehicleBatch> {
    const response = await this.http.get<VehicleBatch>(
      `${this.url}/batch/${batchId}`
    );
    if (!response.success) throw new Error(response.message);
    return response.data;
  }

  async uploadBatch(file: File): Promise<VehicleBatch> {
    const response = await this.http.postWithFile<VehicleBatch>(
      `${this.url}/batch/upload`,
      null,
      file
    );

    if (!response.success) throw new Error(response.message);
    return response.data;
  }

  async processBatch(batchId: string): Promise<void> {
    const response = await this.http.post<void>(
      `${this.url}/batch/process/${batchId}`,
      {}
    );
    if (!response.success) throw new Error(response.message);
    return response.data;
  }

  // override methods which are not available for this service

  override create(data: Partial<Vehicle>): Promise<Vehicle> {
    throw new Error('Method not available.');
  }
  override update(data: Partial<Vehicle>): Promise<Vehicle> {
    throw new Error('Method not available.');
  }
  override delete(id: string): Promise<string> {
    throw new Error('Method not available.');
  }
}
